import { useEffect, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { useDesign } from "../../lib/store";
import { effectiveFinishes } from "../../lib/finishes/effective";
import { ceilingFinish, colorName, floorFinish, wallFinish } from "../../lib/finishes/catalog";
import { requestFinishes } from "../../lib/finishesRequests";
import { reportFinishesEntryShown } from "../../lib/finishesTelemetry";
import { ROOM_FINISHES_FLAG, useFlag } from "../../lib/flags";
import { cn } from "../../lib/cn";
import { FinishArt } from "./FinishTile";

/**
 * Room details' one-line door to finishes: the floor / walls / ceiling
 * swatches and "Oak floor · Warm white walls" ("Deck · Wood fence" for a
 * yard). `onOpen` closes the host sheet so the Finishes sheet isn't stacked
 * on top of it.
 */
export function RoomFinishesRow({ onOpen }: { onOpen: () => void }) {
  const enabled = useFlag(ROOM_FINISHES_FLAG);
  const room = useDesign((s) => s.design.room);
  const furniture = useDesign((s) => s.design.furniture);
  const eff = useMemo(() => effectiveFinishes({ room, furniture }), [room, furniture]);

  useEffect(() => {
    if (enabled) reportFinishesEntryShown("shape", "build");
  }, [enabled]);

  if (!enabled) return null;
  const floor = floorFinish(eff.floor.type);
  const walls = wallFinish(eff.walls.type);
  const ceiling = ceilingFinish(eff.ceiling.type);
  const wallWords = walls.id === "paint" ? colorName(walls, eff.walls.color) : walls.label;
  const summary =
    eff.setting === "outdoor"
      ? `${floor.label} · ${walls.id === "open" ? "Open sides" : walls.label}`
      : `${floor.label} floor · ${wallWords} walls`;
  const swatches = [
    { key: "floor", option: floor, color: eff.floor.color },
    { key: "walls", option: walls, color: eff.walls.color },
    { key: "ceiling", option: ceiling, color: eff.ceiling.color },
  ];

  return (
    <button
      type="button"
      onClick={() => {
        onOpen();
        requestFinishes({ source: "shape", view: "build" });
      }}
      data-testid="room-finishes-row"
      className="mt-5 flex w-full items-center gap-3 rounded-2xl p-2.5 pr-3 text-left ring-1 ring-ink-200 transition-colors hover:bg-ink-50 active:bg-ink-100"
    >
      <span className="flex shrink-0" aria-hidden>
        {swatches.map((s, i) => (
          <span key={s.key} className={cn("rounded-full bg-white p-0.5", i > 0 && "-ml-3.5")}>
            <FinishArt option={s.option} color={s.color} className="relative block size-8 rounded-full ring-1 ring-black/10" />
          </span>
        ))}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-body-sm font-semibold text-ink-900">Finishes</span>
        <span className="block truncate text-caption text-ink-500">{summary}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-ink-400" />
    </button>
  );
}
